import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, filter, map, Observable, shareReplay, switchMap, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { OrganizationService } from 'src/app/@shared';
import { v4 as uuidv4 } from 'uuid';
import { DEFAULT_PAGING } from 'src/app/@shared/constants/site.constants';
import { Filter } from 'src/app/@shared/models/filter.model';
import { Offer, OfferGroupDomain, OfferPromo, OfferTagsResponseModel, PromoDomain } from '..';
import { StatusCount } from '../models/status-count.model';
import { SelectionModel } from '@angular/cdk/collections';
import { CloneType } from '../models/clone-type.model';
import { IPrintPage } from '../models/print-page.model';
import { WorkflowStage } from '../models/workflow-stage.model';
import { IACopyError, IAGenerationOutcome } from '../models/ia-generation-outcome.model';

@Injectable({
  providedIn: 'root',
})
export class OfferPromoService<TOfferPromo extends OfferPromo, TPromoDomain extends PromoDomain<OfferPromo>> {
  private saveorCancel = new BehaviorSubject<string>('');
  private readonly offerEditUndoEnabledSubject = new BehaviorSubject<boolean>(true);
  private readonly offerEditUndoRequestSubject = new BehaviorSubject<number>(0);
  private readonly offerEditCanUndoSubject = new BehaviorSubject<boolean>(false);

  // initialize behavior subjects
  private eventIdBehaviorSubject = new BehaviorSubject<string>('');
  private offerIdBehaviorSubject = new BehaviorSubject<string>('');
  private offerGroupIdBehaviorSubject = new BehaviorSubject<string>('');
  private pageBehaviorSubject = new BehaviorSubject(DEFAULT_PAGING);
  public loadingBehaviorSubject = new BehaviorSubject<boolean>(false);
  public commentDialogBoxStatusBehaviorSubject = new BehaviorSubject<boolean>(false);
  private searchBehaviorSubject = new BehaviorSubject<string>('');
  private uploadPromosPageBehaviorSubject = new BehaviorSubject(DEFAULT_PAGING);
  private uploadPromosSearchBehaviorSubject = new BehaviorSubject<string>('');
  private uploadPromosSortBehaviorSubject = new BehaviorSubject({ active: 'Rank', direction: 'asc', });
  private uploadOfferGroupsPageBehaviorSubject = new BehaviorSubject(DEFAULT_PAGING);
  private uploadOfferGroupsSearchBehaviorSubject = new BehaviorSubject<string>('');
  private uploadOfferGroupsSortBehaviorSubject = new BehaviorSubject({ active: 'Name', direction: 'asc', });
  private uploadOfferGroupsTotalRecordsCount = new BehaviorSubject<number>(0);
  private sortBehaviorSubject = new BehaviorSubject({ active: 'PageNumber', direction: 'asc', });
  private childVersionsSortBehaviorSubject = new BehaviorSubject({ active: 'Name', direction: 'asc', });
  private reloadBehaviorSubject = new BehaviorSubject<string>('');
  private reloadPromoVersionsBehaviorSubject = new BehaviorSubject<string>('');
  private viewModeBehaviorSubject = new BehaviorSubject<string>('CARDS');
  private filterBehaviorSubject = new BehaviorSubject<Filter[]>([]);
  private totalRecordsCount = new BehaviorSubject<number>(0);
  private totalPromoRecordsCount = new BehaviorSubject<number>(0);
  private statusUpdated = new BehaviorSubject<Date>(new Date());
  private offerDetailsReadOnlyMode = new BehaviorSubject<any>(null);
  private cloneEventIdBehaviorSubject = new BehaviorSubject<string>('');
  private cloneSearchBehaviorSubject = new BehaviorSubject<string>('');
  private cloneEntityTypeBehaviorSubject = new BehaviorSubject<string>(CloneType.EVENT);
  private cloneOffersPageBehaviorSubject = new BehaviorSubject(DEFAULT_PAGING);
  private cloneOffersPrintPagesBehaviorSubject = new BehaviorSubject<string[]>([]);
  private cloneOffersTotalRecordsCount = new BehaviorSubject<number>(0);
  private offerGrpPageBehaviorSubject = new BehaviorSubject(DEFAULT_PAGING);
  private offerGrpSearchBehaviorSubject = new BehaviorSubject<string>('');
  private offerGrpSortBehaviorSubject = new BehaviorSubject({ active: 'Rank', direction: 'asc', });
  private offerGrpReloadBehaviorSubject = new BehaviorSubject<string>('');
  private offerGrptotalRecordsCount = new BehaviorSubject<number>(0);
  private offerGrpviewModeBehaviorSubject = new BehaviorSubject<string>('CARDS');
  private jaiCreativeDetailsBehaviorSubject = new BehaviorSubject(null);
  private jaiCreativeApproved = new BehaviorSubject(null);
  private iaCopyOutcomeBehaviorSubject = new BehaviorSubject<IAGenerationOutcome | null>(null);
  private iaCopyProcessingBehaviorSubject = new BehaviorSubject<boolean>(false);
  private iaCopyErrorBehaviorSubject = new BehaviorSubject<IACopyError | null>(null);
  private iaCopyReviewPendingBehaviorSubject = new BehaviorSubject<boolean>(false);
  private iaCopyAwaitingSaveBehaviorSubject = new BehaviorSubject<boolean>(false);
  private readonly settledIACopyRequestIds = new Set<string>();
  private iaCopySourceOfferId: string | null = null;
  private deleteRecordSubject = new BehaviorSubject<string>('');
  private offerDetailsValidStateBehaviorSubject = new BehaviorSubject<boolean>(false);

  // we do not wish to expose our behavior subjects.  create public observables
  public commentDialogBoxStatus$ = this.commentDialogBoxStatusBehaviorSubject.asObservable();
  public page$ = this.pageBehaviorSubject.asObservable();
  public search$ = this.searchBehaviorSubject.asObservable();
  public sort$ = this.sortBehaviorSubject.asObservable();
  public uploadPromosPage$ = this.uploadPromosPageBehaviorSubject.asObservable();
  public uploadPromosSearch$ = this.uploadPromosSearchBehaviorSubject.asObservable();
  public uploadPromosSort$ = this.uploadPromosSortBehaviorSubject.asObservable();
  public uploadOfferGroupsPage$ = this.uploadOfferGroupsPageBehaviorSubject.asObservable();
  public uploadOfferGroupsSearch$ = this.uploadOfferGroupsSearchBehaviorSubject.asObservable();
  public uploadOfferGroupsSort$ = this.uploadOfferGroupsSortBehaviorSubject.asObservable();
  public uploadOfferGroupsTotalRecordsCount$ = this.uploadOfferGroupsTotalRecordsCount.asObservable();
  public childVersionsSort$ = this.childVersionsSortBehaviorSubject.asObservable();
  public isLoading$ = this.loadingBehaviorSubject.asObservable();
  public viewMode$ = this.viewModeBehaviorSubject.asObservable();
  public eventId$ = this.eventIdBehaviorSubject.asObservable();
  public offerId$ = this.offerIdBehaviorSubject.asObservable();
  public offerGroupId$ = this.offerGroupIdBehaviorSubject.asObservable();
  public filters$ = this.filterBehaviorSubject.asObservable();
  public saveorCancel$ = this.saveorCancel.asObservable();
  public totalRecordsCount$ = this.totalRecordsCount.asObservable();
  public totalPromoRecordsCount$ = this.totalPromoRecordsCount.asObservable();
  public statusUpdated$ = this.statusUpdated.asObservable();
  public selection = new SelectionModel<OfferPromo>(true, []);
  public offerDetailsReadOnlyMode$ = this.offerDetailsReadOnlyMode.asObservable();
  public cloneEventId$ = this.cloneEventIdBehaviorSubject.asObservable();
  public cloneSearch$ = this.cloneSearchBehaviorSubject.asObservable();
  public cloneEntityType$ = this.cloneEntityTypeBehaviorSubject.asObservable();
  public cloneOffersPage$ = this.cloneOffersPageBehaviorSubject.asObservable();
  public cloneOffersPrintPages$ = this.cloneOffersPrintPagesBehaviorSubject.asObservable();
  public cloneOffersTotalRecordsCount$ = this.cloneOffersTotalRecordsCount.asObservable();
  public offerGrpPage$ = this.offerGrpPageBehaviorSubject.asObservable();
  public offerGrpSearch$ = this.offerGrpSearchBehaviorSubject.asObservable();
  public offerGrpSort$ = this.offerGrpSortBehaviorSubject.asObservable();
  public offerGrptotalRecordsCount$ = this.offerGrptotalRecordsCount.asObservable();
  public offerGrpviewMode$ = this.offerGrpviewModeBehaviorSubject.asObservable();
  public jaiCreativeDetails$ = this.jaiCreativeDetailsBehaviorSubject.asObservable();
  public jaiCreativeApproved$ = this.jaiCreativeApproved.asObservable();
  public iaCopyOutcome$ = this.iaCopyOutcomeBehaviorSubject.asObservable();
  public iaCopyProcessing$ = this.iaCopyProcessingBehaviorSubject.asObservable();
  public iaCopyError$ = this.iaCopyErrorBehaviorSubject.asObservable();
  public iaCopyReviewPending$ = this.iaCopyReviewPendingBehaviorSubject.asObservable();
  public iaCopyAwaitingSave$ = this.iaCopyAwaitingSaveBehaviorSubject.asObservable();
  public deleteRecord$ = this.deleteRecordSubject.asObservable();
  public offerDetailsValidState$ = this.offerDetailsValidStateBehaviorSubject.asObservable();
  public offerEditUndoEnabled$ = this.offerEditUndoEnabledSubject.asObservable();
  public offerEditUndoRequest$ = this.offerEditUndoRequestSubject.asObservable();
  public offerEditCanUndo$ = this.offerEditCanUndoSubject.asObservable();

  constructor(
    private httpClient: HttpClient,
    private organizationService: OrganizationService
  ) { }

  setOfferEditUndoEnabled(enabled: boolean) {
    this.offerEditUndoEnabledSubject.next(!!enabled);
  }

  requestOfferEditUndo() {
    this.offerEditUndoRequestSubject.next(Date.now());
  }

  setOfferEditCanUndo(canUndo: boolean) {
    this.offerEditCanUndoSubject.next(!!canUndo);
  }

  // create the parameters observable that looks for changes in cloning modal.
  public cloneOfferPromosParams$ = combineLatest([
    this.cloneEventIdBehaviorSubject,
    this.cloneSearchBehaviorSubject.pipe(debounceTime(300)),
    this.cloneEntityTypeBehaviorSubject,
    this.cloneOffersPageBehaviorSubject,
    this.cloneOffersPrintPagesBehaviorSubject
  ]).pipe(
    distinctUntilChanged((previous, current) => {
      // if the values coming down this pipe are the same, don't continue the pipe
      return JSON.stringify(previous) === JSON.stringify(current);
    }),
    filter(([eventId, search, entityType, page]) => {
      //if the entityType is either OFFER or PROMO, pipe continues
      return (entityType === CloneType.OFFER || entityType === CloneType.PROMO || entityType === CloneType.OFFERGROUP)
    }),
    map(([eventId, search, entityType, page, printPages]) => {
      // set the query string odata parameters
      let params: HttpParams = new HttpParams({
        fromObject: {
          eventId: eventId,
          $skip: page.pageIndex * page.pageSize,
          $top: page.pageSize,
          $expand: 'Detail',
          $orderby: 'Detail/Rank asc',
          $count: true,
          types: entityType
        },
      });
      if (search.length) {
        params = params.append('$search', `"${search}"`);
      }
      if (printPages.length) {
        printPages.forEach((pageId: string) => {
          params = params.append('printPageIds', pageId);
        });
      }
      return params;
    })
  )

  private galleyParams = new HttpParams();

  // create the parameters observable that looks for changes in page, startDate, endDate, etc
  public params$ = combineLatest([
    this.eventIdBehaviorSubject,
    this.pageBehaviorSubject, // add debounce if we need to wait for user input ex: .pipe(debounceTime(300)),
    this.sortBehaviorSubject,
    this.searchBehaviorSubject.pipe(debounceTime(300)),
    this.filterBehaviorSubject.pipe(debounceTime(50)),
    this.reloadBehaviorSubject,
  ]).pipe(
    distinctUntilChanged((previous, current) => {
      // if the values coming down this pipe are the same, don't continue the pipe
      return JSON.stringify(previous) === JSON.stringify(current);
    }),
    map(([eventId, page, sort, search, filters, reload]) => {
      let _orderby = `Detail/${sort.active} ${sort.direction}`;
      if (sort.active == 'EventType' || sort.active === 'PageNumber') {
        _orderby = `${sort.active} ${sort.direction}`;
      }

      // set the query string odata parameters
      let params: HttpParams = new HttpParams({
        fromObject: {
          eventId: eventId,
          // mode: viewMode,
          $skip: page.pageIndex * page.pageSize,
          $top: page.pageSize,
          $expand: 'Detail,Variants',
          $orderby: _orderby,
          $count: true,
        },
      });

      // if there is a search, add the search to the parameters
      if (search.length) {
        params = params.append('$search', `"${search}"`);
      }

      // if there are filters, add the filters to the parameters
      if (filters.length > 0) {
        params = this.buildFilterParam(filters, params);
      }
      this.galleyParams = params;
      return params;
    })
  );

  public uploadPromoParams$ = combineLatest([
    this.eventIdBehaviorSubject,
    this.uploadPromosPageBehaviorSubject, // add debounce if we need to wait for user input ex: .pipe(debounceTime(300)),
    this.uploadPromosSortBehaviorSubject,
    this.uploadPromosSearchBehaviorSubject.pipe(debounceTime(300)),
    this.reloadBehaviorSubject,
  ]).pipe(
    distinctUntilChanged((previous, current) => {
      // if the values coming down this pipe are the same, don't continue the pipe
      return JSON.stringify(previous) === JSON.stringify(current);
    }),
    map(([eventId, page, sort, search, reload]) => {
      let _orderby = `Detail/${sort.active} ${sort.direction}`;
      if (sort.active == 'EventType') {
        _orderby = `${sort.active} ${sort.direction}`;
      }

      // set the query string odata parameters
      let params: HttpParams = new HttpParams({
        fromObject: {
          eventId: eventId,
          // mode: viewMode,
          $skip: page.pageIndex * page.pageSize,
          $top: page.pageSize,
          $expand: 'Detail',
          $orderby: _orderby,
          $count: true,
        },
      });

      // if there is a search, add the search to the parameters
      if (search.length) {
        params = params.append('$search', `"${search}"`);
      }
      return params;
    })
  );

  public uploadOfferGroupsParams$ = combineLatest([
    this.eventIdBehaviorSubject,
    this.uploadOfferGroupsPageBehaviorSubject,
    this.uploadOfferGroupsSortBehaviorSubject,
    this.uploadOfferGroupsSearchBehaviorSubject.pipe(debounceTime(300)),
    this.reloadBehaviorSubject,
  ]).pipe(
    distinctUntilChanged((previous, current) => {
      return JSON.stringify(previous) === JSON.stringify(current);
    }),
    map(([eventId, page, sort, search, reload]) => {
      let _orderby = `Detail/${sort.active} ${sort.direction}`;
      if (sort.active == 'EventType') {
        _orderby = `${sort.active} ${sort.direction}`;
      }

      let params: HttpParams = new HttpParams({
        fromObject: {
          eventId: eventId,
          types: 'OFFERGROUP',
          $skip: page.pageIndex * page.pageSize,
          $top: page.pageSize,
          $expand: 'Detail',
          $orderby: _orderby,
          $count: true,
        },
      });

      if (search.length) {
        params = params.append('$search', `"${search}"`);
      }
      return params;
    })
  );

  public promoVersionsparams$ = combineLatest([
    this.offerIdBehaviorSubject,
    this.childVersionsSortBehaviorSubject,
    this.searchBehaviorSubject.pipe(debounceTime(300)),
    this.reloadPromoVersionsBehaviorSubject,
  ]).pipe(
    distinctUntilChanged((previous, current) => {
      // if the values coming down this pipe are the same, don't continue the pipe
      return JSON.stringify(previous) === JSON.stringify(current);
    }),
    map(([offerId, sort, search, reload]) => {
      // set the query string odata parameters
      let params: HttpParams = new HttpParams({
        fromObject: {
          key: offerId,
          $expand: 'Detail',
          $orderby: `Detail/${sort.active} ${sort.direction}`,
          $count: true,
        },
      });

      // if there is a search, add the search to the parameters
      if (search.length) {
        params = params.append('$search', `"${search}"`);
      }

      return params;
    })
  );

  public filterParams$ = combineLatest([
    this.eventIdBehaviorSubject,
    this.pageBehaviorSubject, // add debounce if we need to wait for user input ex: .pipe(debounceTime(300)),
    this.sortBehaviorSubject,
    this.searchBehaviorSubject.pipe(debounceTime(300)),
    this.filterBehaviorSubject.pipe(debounceTime(50)),
    this.reloadBehaviorSubject,
  ]).pipe(
    distinctUntilChanged((previous, current) => {
      // if the values coming down this pipe are the same, don't continue the pipe
      return JSON.stringify(previous) === JSON.stringify(current);
    }),
    map(([eventId, page, sort, search, filters, reload]) => {
      // set the query string odata parameters
      let params: HttpParams = new HttpParams({
        fromObject: {
          eventId: eventId,
          // mode: viewMode,
          $skip: page.pageIndex * page.pageSize,
          $top: page.pageSize,
          $expand: 'Detail',
          $orderby: sort.active === 'PageNumber' ? `${sort.active} ${sort.direction}` : `Detail/${sort.active} ${sort.direction}`,
          $count: true,
          $filter: `EventType eq 'PROMO'`,
          types: `PROMO`,
        },
      });

      // if there is a search, add the search to the parameters
      if (search.length) {
        params = params.append('$search', `"${search}"`);
      }

      // if there are filters, add the filters to the parameters
      if (filters.length > 0) {
        params = this.buildFilterParam(filters, params);
      }
      return params;
    })
  );

  // create the parameters observable that looks for changes in page, startDate, endDate, etc
  public offerGrpparams$ = combineLatest([
    this.eventIdBehaviorSubject,
    this.offerGroupIdBehaviorSubject,
    this.offerGrpPageBehaviorSubject, // add debounce if we need to wait for user input ex: .pipe(debounceTime(300)),
    this.offerGrpSortBehaviorSubject,
    this.offerGrpSearchBehaviorSubject.pipe(debounceTime(300)),
    this.offerGrpReloadBehaviorSubject,
  ]).pipe(
    distinctUntilChanged((previous, current) => {
      // if the values coming down this pipe are the same, don't continue the pipe
      return JSON.stringify(previous) === JSON.stringify(current);
    }),
    map(([eventId, offerId, page, sort, search, reload]) => {
      let _orderby = `Detail/${sort.active} ${sort.direction}`;
      if (sort.active == 'EventType') {
        _orderby = `${sort.active} ${sort.direction}`;
      }

      // set the query string odata parameters
      let params: HttpParams = new HttpParams({
        fromObject: {
          eventId: eventId,
          offerGroupId: offerId,
          // mode: viewMode,
          $skip: page.pageIndex * page.pageSize,
          $top: page.pageSize,
          $expand: 'Detail,Variants',
          $orderby: _orderby,
          $count: true,
        },
      });

      // if there is a search, add the search to the parameters
      if (search.length) {
        params = params.append('$search', `"${search}"`);
      }

      return params;
    })
  );

  set cloneOffersPrintPages(printPages: string[]) {
    this.cloneOffersPrintPagesBehaviorSubject.next(printPages);
  }

  set setOfferDetailsValidState(state: boolean) {
    this.offerDetailsValidStateBehaviorSubject.next(state);
  }

  set eventId(eventId: string) {
    this.eventIdBehaviorSubject.next(eventId);
  }

  set offerId(offerId: string) {
    this.offerIdBehaviorSubject.next(offerId);
  }

  set offerGroupId(offerGroupId: string) {
    this.offerGroupIdBehaviorSubject.next(offerGroupId);
  }

  set cloneEventId(eventId: string) {
    this.cloneEventIdBehaviorSubject.next(eventId);
  }

  public setSaveorCancel(currentAction: string) {
    this.saveorCancel.next(currentAction);
  }

  public updateRecordStatus(date: Date) {
    this.statusUpdated.next(date);
  }

  // get the offers by eventId
  private offerPromoResponse$ = this.params$.pipe(
    tap(() => this.loadingBehaviorSubject.next(true)), // set isLoading to true
    switchMap((_params) =>
      this.httpClient.get(
        `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/PromoDomains/GetByEventId`,
        { params: _params }
      )
    ),
    tap(() => this.loadingBehaviorSubject.next(false)), // set isLoading to false
    shareReplay(1) // make sure all subscriptions share the same http call (otherwise there will be a http call for each subscription)
  );

  // get the offers by eventId
  private cloneOfferPromosResponse$ = this.cloneOfferPromosParams$.pipe(
    tap(() => this.loadingBehaviorSubject.next(true)), // set isLoading to true
    switchMap((_params) => {
      let entityEndpoint = 'PromoDomains/GetByEventId';
      return this.httpClient.get(
        `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/${entityEndpoint}`,
        { params: _params }
      )
    }),
    tap(() => this.loadingBehaviorSubject.next(false)), // set isLoading to false
    shareReplay(1) // make sure all subscriptions share the same http call (otherwise there will be a http call for each subscription)
  );

  // get the promos by eventId
  private uploadPromosResponse$ = this.uploadPromoParams$.pipe(
    tap(() => this.loadingBehaviorSubject.next(true)), // set isLoading to true
    switchMap((_params) =>
      this.httpClient.get(
        `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/PromoDomains/GetByEventId`,
        { params: _params }
      )
    ),
    tap(() => this.loadingBehaviorSubject.next(false)), // set isLoading to false
    shareReplay(1) // make sure all subscriptions share the same http call (otherwise there will be a http call for each subscription)
  );

  private uploadOfferGroupsResponse$ = this.uploadOfferGroupsParams$.pipe(
    tap(() => this.loadingBehaviorSubject.next(true)),
    switchMap((_params) =>
      this.httpClient.get(
        `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/OfferGroupDomains/SearchAllOfferCopiesByEvent`,
        { params: _params }
      )
    ),
    tap(() => this.loadingBehaviorSubject.next(false)),
    shareReplay(1)
  );

  private searchAllEventOfferTypesByEventId$ = this.params$.pipe(
    tap(() => this.loadingBehaviorSubject.next(true)), // set isLoading to true
    switchMap((_params) =>
      this.httpClient.get(
        `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/OfferGroupDomains/SearchAllOfferCopiesByEvent`,
        { params: _params }
      )
    ),
    tap(() => this.loadingBehaviorSubject.next(false)), // set isLoading to false
    shareReplay(1) // make sure all subscriptions share the same http call (otherwise there will be a http call for each subscription)
  );

  private typeIdsByEventInitialData: any = null;

  setTypeIdsByEventInitialData(res: any): void {
    let pageData = this.pageBehaviorSubject.value;
    let totalRecords = res ? res['@odata.count'] : null;
    let data = null;

    if (res?.value?.length && totalRecords) {
      data = new Array(totalRecords).fill(null);
      data.splice(pageData.pageIndex * pageData.pageSize, res.value.length, ...res.value.map(this.transformOfferToVariantList));
    }

    this.typeIdsByEventInitialData = data;
  }

  transformOfferToVariantList(offer: any): any {
    return {
      BaseId: offer.DomainId,
      Label: offer.EventOfferType.charAt(0).toUpperCase() + offer.EventOfferType.slice(1).toLowerCase(),
      VariantIds: offer.Variants?.map((variant: any) => variant.Id),
    };
  }

  getTypeIdsByEventInitialData(): any {
    return this.typeIdsByEventInitialData;
  }

  searchAllEventOfferTypeIdsByEvent(page: any): Observable<any> {
    const sort = this.sortBehaviorSubject.value;
    const search = this.searchBehaviorSubject.value;
    const filters = this.filterBehaviorSubject.value;

    let _orderby = `Detail/${sort.active} ${sort.direction}`;
    if (sort.active == 'EventType' || sort.active === 'PageNumber') {
      _orderby = `${sort.active} ${sort.direction}`;
    }

    let params = new HttpParams({
      fromObject: {
        eventId: this.eventIdBehaviorSubject.value,
        $skip: page.pageIndex * page.pageSize,
        $top: page.pageSize,
        $orderby: _orderby,
        $count: true,
      },
    });

    if (search.length) {
      params = params.append('$search', `"${search}"`);
    }

    if (filters.length > 0) {
      params = this.buildFilterParam(filters, params);
    }

    return this.httpClient.get(
      `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/OfferGroupDomains/SearchAllOfferCopyIdsByEvent`,
      { params }
    )
  }

  // offerpromo listing
  public offerPromos$: Observable<TPromoDomain[]> =
    this.offerPromoResponse$.pipe(
      map((res: any) => {
        this.totalRecordsCount.next(res['@odata.count']);
        return res.value;
      })
    );

  // offerpromo listing
  public cloneOfferPromos$: Observable<TPromoDomain[]> =
    this.cloneOfferPromosResponse$.pipe(
      map((res: any) => {
        this.cloneOffersTotalRecordsCount.next(res['@odata.count'])
        return res.value;
      })
    );

  // total number of offer records based on filtering
  public cloneOffersTotalRecords$: Observable<number> = this.cloneOffersTotalRecordsCount$.pipe(
    map((res: any) => res)
  );

  //promo listing
  public uploadPromos$: Observable<TPromoDomain[]> =
    this.uploadPromosResponse$.pipe(
      map((res: any) => {
        this.totalRecordsCount.next(res['@odata.count']);
        return res.value;
      })
    );

  public uploadOfferGroups$: Observable<OfferGroupDomain<Offer>[]> =
    this.uploadOfferGroupsResponse$.pipe(
      map((res: any) => {
        this.uploadOfferGroupsTotalRecordsCount.next(res['@odata.count']);
        return res.value;
      })
    );

  public uploadOfferGroupsTotalRecords$: Observable<number> = this.uploadOfferGroupsTotalRecordsCount$.pipe(
    map((res: any) => res)
  );

  // offerpromo listing
  public getAllEventOfferTypesByEventId$: Observable<OfferGroupDomain<Offer>[]> = this.searchAllEventOfferTypesByEventId$.pipe(
    map((res: any) => {
      this.setTypeIdsByEventInitialData(res);
      this.totalRecordsCount.next(res['@odata.count']);
      return res.value;
    })
  );

  // total number of offer records based on filtering
  public totalRecords$: Observable<number> = this.totalRecordsCount$.pipe(
    map((res: any) => res)
  );

  // get the child versions by offerid
  private promoVersionsResponse$ = this.promoVersionsparams$.pipe(
    tap(() => this.loadingBehaviorSubject.next(true)), // set isLoading to true
    switchMap((_params) =>
      this.httpClient.get(
        `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/PromoDomains/GetVariants`,
        { params: _params }
      )
    ),
    tap(() => this.loadingBehaviorSubject.next(false)), // set isLoading to false
    shareReplay(1) // make sure all subscriptions share the same http call (otherwise there will be a http call for each subscription)
  );

  // offerpromo listing
  public getpromoChildVersions$: Observable<OfferGroupDomain<Offer>[]> =
    this.promoVersionsResponse$.pipe(map((res: any) => (res ? res.value : [])));

  // total number of offer records based on filtering
  public totalVersionsRecords$: Observable<number> =
    this.promoVersionsResponse$.pipe(
      map((res: any) => (res ? res['@odata.count'] : 0))
    );

  // gets an offer by id
  getPromo(promoId: string): Observable<PromoDomain<TOfferPromo>> {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/PromoDomains/${promoId}?$expand=Detail`;
    return this.httpClient.get<PromoDomain<TOfferPromo>>(url);
  }

  // gets an offer by id
  getOfferPromoTags(
    eventId: string,
    offers: string[]
  ): Observable<OfferTagsResponseModel> {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/OfferDomains/GetOfferTagsByOfferIds?eventId=${eventId}`;
    return this.httpClient.post<OfferTagsResponseModel>(url, offers);
  }

  massSaveofferTags(offerTags: any) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/OfferDomains/UpdateOfferTagsByOfferIds`;
    return this.httpClient.post<TOfferPromo>(url, offerTags);
  }

  getStatusCount(): Observable<StatusCount> {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/PromoDomains/StatusCount?eventId=${this.eventIdBehaviorSubject.value}`;
    return this.httpClient.get<StatusCount>(url);
  }

  getVariantDetails(variantId: string, EventOfferType: string): Observable<OfferGroupDomain<Offer>> {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/${EventOfferType === "OFFER" ? 'OfferDomains' : 'PromoDomains'}/GetPreview?key=${variantId}&$expand=Detail`;
    return this.httpClient.get<OfferGroupDomain<Offer>>(url);
  }

  // set the current page
  page(page: any) {
    this.pageBehaviorSubject.next(page);
  }

  getPageDetails() {
    return this.pageBehaviorSubject.value;
  }

  cloneOffersPage(page: any) {
    this.cloneOffersPageBehaviorSubject.next(page);
  }

  // set the current page
  uploadPromosPage(page: any) {
    this.uploadPromosPageBehaviorSubject.next(page);
  }

  uploadOfferGroupsPage(page: any) {
    this.uploadOfferGroupsPageBehaviorSubject.next(page);
  }

  getAssets() {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/AssetDomains?$expand=Detail&$filter=assetgroupname eq 'Badges'`;
    return this.httpClient.get(url);
  }

  savePromo(PromoDomain: PromoDomain<TOfferPromo>) {
    let url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/PromoDomains/Post`;
    return this.httpClient.post(url, PromoDomain);
  }

  savePromoProperties(promoIds: string[], properties: any) {
    let url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath
      }/${this.organizationService.organization?.version
      }/PromoDomains/UpdatePromoProperties?${this.preparePromoIdString(
        promoIds
      )}`;
    return this.httpClient.post(url, properties);
  }

  preparePromoIdString(promoIds: any) {
    let urlParams = '';
    if (promoIds && promoIds.length > 0) {
      for (let index = 0; index <= promoIds.length - 1; index++) {
        urlParams += `promoIds=${promoIds[index]}`;
        if (index != promoIds.length - 1) {
          urlParams += '&';
        }
      }
    }
    return urlParams;
  }

  // deletes an Offer by id
  deletePromo(id: string, eventId: string) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/PromoDomains/${id}?eventId=${eventId}`;
    return this.httpClient.delete(url);
  }

  // deletes an Offer by id
  deleteBasePromo(id: string, eventId: string) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/PromoDomains/DeletePromoVersions?key=${id}&eventId=${eventId}`;
    return this.httpClient.delete(url);
  }

  clonePromo(sourceId: string, PromoDomain: PromoDomain<TOfferPromo>) {
    let url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/PromoDomains/ClonePromo?sourceId=${sourceId}`;
    return this.httpClient.post(url, PromoDomain);
  }

  // sets the sort property and order
  sort(sort: any) {
    this.sortBehaviorSubject.next(sort);
  }

  // sets the sort property and order
  uploadPromosSort(sort: any) {
    this.uploadPromosSortBehaviorSubject.next(sort);
  }

  uploadOfferGroupsSort(sort: any) {
    this.uploadOfferGroupsSortBehaviorSubject.next(sort);
  }

  childVersionsSort(sort: any) {
    this.childVersionsSortBehaviorSubject.next(sort);
  }

  // sets the search phrase
  uploadPromosSearch(search: string) {
    const page = this.uploadPromosPageBehaviorSubject.value;
    page.pageIndex = 0;
    page.previousPageIndex = 0;
    this.uploadPromosSearchBehaviorSubject.next(search);
    this.uploadPromosPageBehaviorSubject.next(page);
  }

  uploadOfferGroupsSearch(search: string) {
    const page = this.uploadOfferGroupsPageBehaviorSubject.value;
    page.pageIndex = 0;
    page.previousPageIndex = 0;
    this.uploadOfferGroupsSearchBehaviorSubject.next(search);
    this.uploadOfferGroupsPageBehaviorSubject.next(page);
  }

  // sets the search phrase
  search(search: string) {
    const page = this.pageBehaviorSubject.value;
    page.pageIndex = 0;
    page.previousPageIndex = 0;
    this.searchBehaviorSubject.next(search);
    this.pageBehaviorSubject.next(page);
  }

  cloneSearch(search: string) {
    const page = { ...this.cloneOffersPageBehaviorSubject.value, pageIndex: 0, previousPageIndex: 0 };
    this.cloneSearchBehaviorSubject.next(search);
    this.cloneOffersPageBehaviorSubject.next(page);
  }

  resetClonePage() {
    const page = { ...this.cloneOffersPageBehaviorSubject.value, pageIndex: 0, previousPageIndex: 0 };
    this.cloneOffersPageBehaviorSubject.next(page);
  }

  cloneEntity(type: string) {
    this.cloneEntityTypeBehaviorSubject.next(type);
    this.resetClonePage();
  }


  // reloads/refreshes the offer listing
  reload() {
    // reload the Offer data
    this.reloadBehaviorSubject.next(uuidv4());
  }
  reloadPromoVersions() {
    // reload the Offer data
    this.reloadPromoVersionsBehaviorSubject.next(uuidv4());
  }

  // changes the view mode of the offer listing
  toggleViewMode(mode: string) {
    this.viewModeBehaviorSubject.next(mode);
  }

  // changes the view mode of the offer listing
  toggleOfferGrpViewMode(mode: string) {
    this.offerGrpviewModeBehaviorSubject.next(mode);
  }

  // adds filters to the event listing
  addFilters(newFilters: Filter[]) {
    const filters = this.filterBehaviorSubject.value;

    newFilters.forEach((filter) => {
      if (
        filters.findIndex(
          (item) =>
            item.fieldName.toLowerCase() === filter.fieldName.toLowerCase() &&
            item.value.toLowerCase() === filter.value.toLowerCase()
        ) === -1
      ) {
        filters.push(filter);
      }
    });

    this.filterBehaviorSubject.next(filters);
  }

  // removes a filter from the event listing
  removeFilter(filter: Filter) {
    const filters = this.filterBehaviorSubject.value.filter(
      (item) => item !== filter
    );
    this.filterBehaviorSubject.next(filters);
  }

  // removes a filter from the event listing
  removeFilterByFieldName(fieldName: string) {
    const filters = this.filterBehaviorSubject.value.filter(
      (item) => item.fieldName.toLowerCase() !== fieldName.toLowerCase()
    );
    this.filterBehaviorSubject.next(filters);
  }

  // removes all filters for the event listing
  clearFilters() {
    this.filterBehaviorSubject.next([]);
  }

  // builds the filter expressions for filtering the event listing
  private buildFilterParam(filters: Filter[], params: HttpParams): HttpParams {
    // build the offerTagId expression
    const offerTagIdFilter = filters.filter(
      (item) => item.fieldName.toLowerCase() === 'offertagid'
    );

    // loop through the division id filters and add filter statement to param
    offerTagIdFilter.forEach((filter, index) => {
      params = params.append('offerTagIds', `${filter.value}`);
    });

    // build the versionIds expression
    const versionIdFilters = filters.filter(
      (item) => item.fieldName.toLowerCase() === 'versionid'
    );
    // loop through the version id filters and add filter statement to param
    versionIdFilters.forEach((filter, index) => {
      params = params.append('versionIds', `${filter.value}`);
    });

    // build the notags expression
    const noTagFilters = filters.filter(
      (item) => item.fieldName.toLowerCase() === 'notags'
    );
    // loop through the notags filters and add filter statement to param
    noTagFilters.forEach((filter, index) => {
      params = params.append('noTags', `${filter.value}`);
    });

    // build the notags expression
    const categoryFilters = filters.filter(
      (item) => item.fieldName.toLowerCase() === 'categoryid'
    );
    // loop through the categoryId filters and add filter statement to param
    categoryFilters.forEach((filter, index) => {
      params = params.append('categoryIds', `${filter.value}`);
    });

    //build commentStatuses expression
    const commentStatusFilters = filters.filter(
      (item) => item.fieldName.toLowerCase() === 'commentstatuses'
    );

    // loop through the commentStatuses filters and add filter statement to param
    commentStatusFilters.forEach((filter, index) => {
      params = params.append('CommentStatuses', `${filter.value}`);
    });

    //build workflowStages expression
    const workflowStageFilters = filters.filter(
      (item) => item.fieldName.toLowerCase() === 'workflowstages'
    );
    // loop through the workflowStages filters and add filter statement to param
    workflowStageFilters.forEach((filter, index) => {
      params = params.append('WorkflowTaskIds', `${filter.value}`);
    });

    // build the notags expression
    const hasCommentsFilters = filters.filter(
      (item) => item.fieldName.toLowerCase() === 'hascomments'
    );
    // loop through the notags filters and add filter statement to param
    hasCommentsFilters.forEach((filter, index) => {
      params = params.append('hasComments', `${filter.value}`);
    });
    // build the versionIds expression
    const eventIdFilters = filters.filter(
      (item) => item.fieldName.toLowerCase() === 'eventtype'
    );
    // loop through the version id filters and add filter statement to param
    eventIdFilters.forEach((filter, index) => {
      params = params.append('types', `${filter.value}`);
    });

    // build the statusType expression
    const statusTypeFilters = filters.filter(
      (item) => item.fieldName.toLowerCase() === 'status'
    );
    // loop through the status filters and add filter statement to param
    statusTypeFilters.forEach((filter, index) => {
      params = params.append('statuses', `${filter.value}`);
    });

    // build the pageIds expression
    const pageIdsFilters = filters.filter(
      (item) => item.fieldName.toLowerCase() === 'pageids'
    );
    // loop through the status filters and add filter statement to param
    pageIdsFilters.forEach((filter, index) => {
      params = params.append('pageIds', `${filter.value}`);
    });

    // build the has variants expression
    const hasVariantsFilters = filters.filter(
      (item) => item.fieldName.toLowerCase() === 'hasvariants'
    );
    // loop through the hasVariants filters and add filter statement to param
    hasVariantsFilters.forEach((filter, index) => {
      params = params.append('hasVariants', `${filter.value}`);
    });

    // build the noVersions expression
    const noVersionsFilters = filters.filter(
      (item) => item.fieldName.toLowerCase() === 'noversions'
    );
    // loop through the noVersions filters and add filter statement to param
    noVersionsFilters.forEach((filter, index) => {
      params = params.append('noVersions', `${filter.value}`);
    });

    // build the hasTranslation expression
    const hasTranslationFilters = filters.filter(
      (item) => item.fieldName.toLowerCase() === 'hastranslation'
    );
    // loop through the hasTranslation filters and add filter statement to param
    hasTranslationFilters.forEach((filter, index) => {
      params = params.append('hasTranslation', `${filter.value}`);
    });

    return params;
  }

  updateStatus(status: any) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/PromoDomains/UpdateStatus`;
    return this.httpClient.post(url, status);
  }

  updateDates(updateDatesList: any) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/PromoDomains/UpdateDates`;
    return this.httpClient.post(url, updateDatesList);
  }

  deleteOfferPromos(promos: any) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/PromoDomains/DeletePromos`;
    return this.httpClient.post(url, promos);
  }

  downloadOffersJSON(eventId: string) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Offers/EtlExport?eventId=${eventId}`;
    window.open(url, '_blank');
  }

  downloadOfferGroupJSON(eventId: string) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/OfferGroupDomains/EtlExport?eventId=${eventId}`;
    window.open(url, '_blank');
  }

  downloadPromosJSON(eventId: string) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Promos/EtlExport?eventId=${eventId}`;
    window.open(url, '_blank');
  }

  downloadEventJSON(eventId: string) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Events/EtlExportEvent?eventId=${eventId}`;
    window.open(url, '_blank');
  }

  downloadCSV(eventId: string, ids?: string[]) {
    this.galleyParams = this.galleyParams.set('eventId', eventId);
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Events/ExportOffers`;
    return this.httpClient.post(url, ids ?? [], { responseType: 'blob', observe: 'response', params: this.galleyParams });
  }

  downloadCommentsCSV(eventId: string) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Events/ExportComments?eventId=${eventId}&timestamp=${Date.now()}`;
    window.open(url, '_blank');
  }

  setOfferDetailsReadOnly(val: boolean | null) {
    this.offerDetailsReadOnlyMode.next(val);
  }

  getOfferDetailsReadOnlyValue(): boolean {
    return this.offerDetailsReadOnlyMode.value;
  }

  cloneOfferPromos(req: any) {
    const entityEndpoint =
      this.cloneEntityTypeBehaviorSubject.value === CloneType.OFFER ? 'CloneOffers' : this.cloneEntityTypeBehaviorSubject.value === CloneType.PROMO ? 'ClonePromos'
        : this.cloneEntityTypeBehaviorSubject.value === CloneType.OFFERGROUP ? 'CloneOfferGroups' : '';

    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/EventDomains/${entityEndpoint}`;
    return this.httpClient.post(url, req);
  }

  ExportSelectedGalleyProof(eventId: string, offerIds: string[]) {
    this.galleyParams = this.galleyParams.set('eventId', eventId);
    this.galleyParams = this.galleyParams.set('mode', this.viewModeBehaviorSubject.value.toLowerCase());
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/EventDomains/ExportGalleyProof`;
    return this.httpClient.post(url, offerIds, { responseType: 'blob', observe: 'response', params: this.galleyParams });
  }

  exportAllGalleyProof(eventId: string) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/EventDomains/ExportGalleyProof?eventId=${eventId}&mode=${this.viewModeBehaviorSubject.value.toLowerCase()}`;
    return this.httpClient.post(url, {}, { responseType: 'blob', observe: 'response', params: this.galleyParams })
  }

  private offerGrpOffersResponse$ = this.offerGrpparams$.pipe(
    tap(() => this.loadingBehaviorSubject.next(true)), // set isLoading to true
    switchMap((_params) =>
      this.httpClient.get(
        `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/offerdomains/GetOffersByOfferGroupId`,
        { params: _params }
      )
    ),
    tap(() => this.loadingBehaviorSubject.next(false)), // set isLoading to false
    shareReplay(1) // make sure all subscriptions share the same http call (otherwise there will be a http call for each subscription)
  );

  // offerpromo listing
  public getofferGrpOffers$: Observable<TPromoDomain[]> = this.offerGrpOffersResponse$.pipe(
    map((res: any) => {
      this.offerGrptotalRecordsCount.next(res['@odata.count']);
      return res.value;
    })
  );

  // total number of offer records based on filtering
  public offerGrptotalRecords$: Observable<number> = this.offerGrptotalRecordsCount$.pipe(
    map((res: any) => res)
  );

  // set the current page
  offerGrpPage(page: any) {
    this.offerGrpPageBehaviorSubject.next(page);
  }

  // set the current page
  resetOfferGrpPage() {
    this.offerGrpPageBehaviorSubject.next(DEFAULT_PAGING);
  }

  // sets the sort property and order
  offerGrpSort(sort: any) {
    this.offerGrpSortBehaviorSubject.next(sort);
  }

  jaiCreativeInfo(details: any) {
    this.jaiCreativeDetailsBehaviorSubject.next(details);
  }

  approveJAICreative(details: any) {
    this.jaiCreativeApproved.next(details);
  }

  setIACopyOutcome(outcome: IAGenerationOutcome | null) {
    this.iaCopyOutcomeBehaviorSubject.next(outcome);
  }

  setIACopyProcessing(processing: boolean) {
    this.iaCopyProcessingBehaviorSubject.next(!!processing);
  }

  setIACopyError(error: IACopyError | null) {
    this.iaCopyErrorBehaviorSubject.next(error);
  }

  setIACopyReviewPending(pending: boolean) {
    this.iaCopyReviewPendingBehaviorSubject.next(!!pending);
  }

  setIACopyAwaitingSave(awaiting: boolean) {
    this.iaCopyAwaitingSaveBehaviorSubject.next(!!awaiting);
  }

  markIACopyRequestSettled(requestId?: string | null): void {
    const id = requestId?.trim();
    if (id) {
      this.settledIACopyRequestIds.add(id);
    }
  }

  isIACopyRequestSettled(requestId?: string | null): boolean {
    const id = requestId?.trim();
    return !!id && this.settledIACopyRequestIds.has(id);
  }

  unmarkIACopyRequestSettled(requestId?: string | null): void {
    const id = requestId?.trim();
    if (id) {
      this.settledIACopyRequestIds.delete(id);
    }
  }

  clearSettledIACopyRequestIds(): void {
    this.settledIACopyRequestIds.clear();
  }

  setIACopySourceOfferId(offerId: string | null): void {
    this.iaCopySourceOfferId = offerId?.trim() || null;
  }

  getIACopySourceOfferId(): string | null {
    return this.iaCopySourceOfferId;
  }

  isIACopyForOffer(offerId: string): boolean {
    const sourceId = this.iaCopySourceOfferId?.trim();
    const currentId = offerId?.trim();
    return !!sourceId && !!currentId && sourceId === currentId;
  }

  clearIACopyOutcome() {
    this.iaCopyOutcomeBehaviorSubject.next(null);
    this.iaCopyProcessingBehaviorSubject.next(false);
    this.iaCopyErrorBehaviorSubject.next(null);
    this.iaCopyReviewPendingBehaviorSubject.next(false);
    this.iaCopyAwaitingSaveBehaviorSubject.next(false);
    this.iaCopySourceOfferId = null;
  }

  resetIACopyState() {
    this.clearIACopyOutcome();
  }

  // sets the search phrase
  offerGrpSearch(search: string) {
    const page = this.offerGrpPageBehaviorSubject.value;
    page.pageIndex = 0;
    page.previousPageIndex = 0;
    this.offerGrpSearchBehaviorSubject.next(search);
    this.offerGrpPageBehaviorSubject.next(page);
  }

  // reloads/refreshes the offer listing
  offerGrpReload() {
    // reload the Offer data
    this.offerGrpReloadBehaviorSubject.next(uuidv4());
  }

  deleteOfferGroup(offerGroupId: string) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/OfferGroupDomains/${offerGroupId}`;
    return this.httpClient.delete(url);
  }

  updateAssociationRankByContext(offerGroupId: string, offerId: string, rank: number) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/OfferGroupDomains/UpdateAssociatedOfferRank?offerGroupId=${offerGroupId}&offerId=${offerId}&rank=${rank}`;
    return this.httpClient.post(url, null);
  }

  associateOffers(offerGroupId: string, offerIds: string[]) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/OfferGroupDomains/AssociateOffers?offerGroupId=${offerGroupId}`;
    return this.httpClient.post(url, offerIds);
  }

  disAssociateOffers(offerGroupId: string, offerIds: string[]) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/OfferGroupDomains/DisAssociateOffers?offerGroupId=${offerGroupId}`;
    return this.httpClient.post(url, offerIds);
  }

  createJAICreative(offerId: string) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/JunctionAI/CreateJAICreative?key=${offerId}`;
    return this.httpClient.put(`${url}`, {});
  }

  getJAICreative(id: string) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/JunctionAI/GetCreativeDetails?key=${id}`;
    return this.httpClient.get(`${url}`);
  }

  GetAllPrintPagesByEventforOffers(eventId: string) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/OfferDomains/GetAllPrintPagesByEvent?eventId=${eventId}`;
    return this.httpClient.get<IPrintPage[]>(`${url}`);
  }

  GetAllPrintPagesByEventforPromos(eventId: string) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/PromoDomains/GetAllPrintPagesByEvent?eventId=${eventId}`;
    return this.httpClient.get<IPrintPage[]>(`${url}`);
  }

  BulkUpdatePrintPage(body: any) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/OfferDomains/BulkUpdatePrintPage`
    return this.httpClient.post<boolean | any>(url, body);
  }

  deleteRecord(versionName: string) {
    this.deleteRecordSubject.next(versionName);
  }

  private workFlowStagesResponse$ = this.eventId$.pipe(
    switchMap((eventId) => {
      const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Workflow/GetWorkflowStages?featureId=${eventId}`;
      return this.httpClient.get(url);
    })
  );

  public workflowStages$: Observable<WorkflowStage[]> = this.workFlowStagesResponse$.pipe(
    map((res: any) => {
      return JSON.parse(res.value);
    })
  );

  private allWorkFlowStagesRepsonse$ = this.eventId$.pipe(
    switchMap((eventId) => {
      const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Workflow/GetWorkflows?featureId=${eventId}`;
      return this.httpClient.get(url);
    })
  );

  public allWorkflowStages$: Observable<WorkflowStage[]> = this.allWorkFlowStagesRepsonse$.pipe(
    map((res: any) => {
      return res.value;
    })
  )

  SearchAllOfferCopyIdsByEvent(page: any): Observable<any> {
    const sort = this.sortBehaviorSubject.value;
    const search = this.searchBehaviorSubject.value;
    const filters = this.filterBehaviorSubject.value;

    let orderBy = `Detail/${sort.active}`;
    if (sort.active === 'EventType' || sort.active === 'PageNumber') {
      orderBy = sort.active;
    }

    let params = new HttpParams({
      fromObject: {
        eventId: this.eventIdBehaviorSubject.value,
        skip: String(page.pageIndex * page.pageSize),
        limit: String(page.pageSize),
        orderBy,
        orderDirection: sort.direction,
        $count: true,
      },
    });

    if (search.length) {
      params = params.append('$search', `"${search}"`);
    }

    if (filters.length > 0) {
      params = this.buildFilterParam(filters, params);
    }
    return this.httpClient.get(
      `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/OfferGroupDomains/SearchAllOfferCopyIdsByEvent`,
      { params }
    );
  }
}
